import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { getAlumniLookups, getAlumniStats, listAlumni } from '../../api/alumni';
import { useAuth } from '../../context/authContext';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { Alumni, AlumniListParams, AlumniStatus } from '../../types/alumni';
import { ALUMNI_STATUS_COLORS, ALUMNI_STATUS_LABELS, formatDay } from './alumniLabels';
import { AlumniFormDialog } from './AlumniFormDialog';
import { AlumniImportDialog } from './AlumniImportDialog';
import { AlumniExportDialog } from './AlumniExportDialog';

type Filters = Omit<AlumniListParams, 'page' | 'pageSize' | 'sortBy' | 'sortOrder' | 'branchId'>;

const StatCard = ({
  label,
  value,
  hint,
  active,
  onClick,
  color = 'primary.main',
}: {
  label: string;
  value: number | undefined;
  hint?: string;
  active?: boolean;
  onClick?: () => void;
  color?: string;
}) => (
  <Card variant="outlined" sx={{ height: '100%', borderColor: active ? 'primary.main' : undefined, borderWidth: active ? 2 : 1 }}>
    <CardActionArea
      onClick={onClick}
      disabled={!onClick}
      sx={{ height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}
    >
      <CardContent sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {value === undefined ? (
          <Skeleton width={60} height={40} />
        ) : (
          <Typography variant="h4" fontWeight={700} sx={{ color }}>
            {value}
          </Typography>
        )}
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </CardContent>
    </CardActionArea>
  </Card>
);

const Breakdown = ({
  title,
  rows,
  onPick,
}: {
  title: string;
  rows: Array<{ key: string; label: string; count: number; value: string | number | null }>;
  onPick: (value: string | number | null) => void;
}) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No alumni yet.
          </Typography>
        )}
        <Stack spacing={0.75}>
          {rows.slice(0, 8).map((r) => (
            <Box
              key={r.key}
              role={r.value != null ? 'button' : undefined}
              tabIndex={r.value != null ? 0 : undefined}
              onClick={() => r.value != null && onPick(r.value)}
              onKeyDown={(e) => e.key === 'Enter' && r.value != null && onPick(r.value)}
              sx={{ cursor: r.value != null ? 'pointer' : 'default' }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">{r.label}</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {r.count}
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={(r.count / max) * 100} sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

/** Alumni (Phase 105): the dashboard cards and the searchable, filterable
 * alumni register. Cards and breakdowns are clickable shortcuts into the
 * matching filter. Actions follow the user's alumni:* permissions (the API
 * enforces the same). */
export const AlumniListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const [filters, setFilters] = useState<Filters>({ archived: 'active' });
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value === '' ? undefined : value }));
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const sort = sortModel[0];
  const params: AlumniListParams = {
    ...filters,
    branchId: selectedBranchId,
    sortBy: sort?.field === 'countryOfWork' ? 'createdAt' : sort?.field,
    sortOrder: sort?.sort ?? 'desc',
  };
  const listQuery = useQuery({
    queryKey: ['alumni', paginationModel, params],
    queryFn: () => listAlumni({ ...params, page: paginationModel.page + 1, pageSize: paginationModel.pageSize }),
    placeholderData: (prev) => prev,
  });
  const statsQuery = useQuery({ queryKey: ['alumni-stats', selectedBranchId], queryFn: () => getAlumniStats(selectedBranchId) });
  const lookupsQuery = useQuery({ queryKey: ['alumni-lookups'], queryFn: getAlumniLookups });
  const stats = statsQuery.data;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['alumni'] });
    queryClient.invalidateQueries({ queryKey: ['alumni-stats'] });
  };

  const columns: GridColDef<Alumni>[] = [
    { field: 'alumniNumber', headerName: 'Alumni ID', width: 110 },
    { field: 'fullName', headerName: 'Name', flex: 1, minWidth: 180 },
    {
      field: 'studentNumber',
      headerName: 'Student ID',
      width: 120,
      sortable: false,
      valueGetter: (_v, row) => row.student?.studentNumber ?? row.originalStudentNumber ?? '',
    },
    { field: 'phone', headerName: 'Contact', width: 140, sortable: false },
    { field: 'email', headerName: 'Email', width: 200, sortable: false },
    { field: 'employer', headerName: 'Place of work', width: 170, sortable: false },
    { field: 'graduationYear', headerName: 'Graduated', width: 100 },
    {
      field: 'countryOfWork',
      headerName: 'Country of work',
      width: 140,
      sortable: false,
      valueGetter: (_v, row) => row.countryOfWork?.name ?? '',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 170,
      sortable: false,
      renderCell: (p) =>
        p.row.archivedAt ? (
          <Chip size="small" label="Archived" />
        ) : p.row.status ? (
          <Chip size="small" variant="outlined" color={ALUMNI_STATUS_COLORS[p.row.status]} label={ALUMNI_STATUS_LABELS[p.row.status]} />
        ) : (
          '—'
        ),
    },
    {
      field: 'nextFollowUpAt',
      headerName: 'Next follow-up',
      width: 130,
      sortable: false,
      valueFormatter: (v: string | null) => (v ? formatDay(v) : ''),
    },
  ];

  const lookups = lookupsQuery.data;
  const activeChip = (key: 'needsVerification' | 'followUpDue', label: string) =>
    filters[key] ? <Chip label={label} color="primary" onDelete={() => setFilter(key, undefined)} /> : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        icon={WorkspacePremiumIcon}
        title="Alumni"
        subtitle="Graduates and former students — where they work, how to reach them, and follow-ups."
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {hasPermission('alumni', 'export') && (
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => setExportOpen(true)}>
                Export
              </Button>
            )}
            {hasPermission('alumni', 'import') && (
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>
                Import
              </Button>
            )}
            {hasPermission('alumni', 'create') && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                Add Alumni
              </Button>
            )}
          </Stack>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard label="Total alumni" value={stats?.total} onClick={() => setFilters({ archived: 'active' })} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            label="Employed / self-employed"
            value={stats?.employedOrSelfEmployed}
            hint={stats ? `${stats.employed} employed · ${stats.selfEmployed} self-employed` : undefined}
            color="success.main"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            label="Seeking opportunities"
            value={stats?.seekingOpportunities}
            color="warning.main"
            active={filters.status === 'SEEKING_OPPORTUNITIES'}
            onClick={() => setFilter('status', 'SEEKING_OPPORTUNITIES')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            label="Details need verification"
            value={stats?.needsVerification}
            hint="Never verified, over a year old, or missing core details"
            color="error.main"
            active={!!filters.needsVerification}
            onClick={() => setFilter('needsVerification', true)}
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatCard
            label="Follow-ups due"
            value={stats?.followUpsDue}
            hint="Due within 7 days or overdue"
            color="secondary.main"
            active={!!filters.followUpDue}
            onClick={() => setFilter('followUpDue', true)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Breakdown
            title="Alumni by graduation year"
            rows={(stats?.byGraduationYear ?? []).map((r) => ({
              key: String(r.graduationYear),
              label: r.graduationYear ? String(r.graduationYear) : 'Not recorded',
              count: r.count,
              value: r.graduationYear,
            }))}
            onPick={(v) => setFilter('graduationYear', Number(v))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Breakdown
            title="Alumni by country of work"
            rows={(stats?.byCountryOfWork ?? []).map((r) => ({
              key: String(r.countryOfWorkId),
              label: r.country ?? 'Not recorded',
              count: r.count,
              value: r.countryOfWorkId,
            }))}
            onPick={(v) => setFilter('countryOfWorkId', Number(v))}
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Name, Alumni ID, Student ID, email or phone"
              value={filters.search ?? ''}
              onChange={(e) => setFilter('search', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              inputProps={{ 'aria-label': 'Search alumni' }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="University" value={filters.university ?? ''} onChange={(e) => setFilter('university', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Course" value={filters.course ?? ''} onChange={(e) => setFilter('course', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Employer" value={filters.employer ?? ''} onChange={(e) => setFilter('employer', e.target.value)} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Graduation year"
              value={filters.graduationYear ?? ''}
              inputProps={{ inputMode: 'numeric', maxLength: 4 }}
              onChange={(e) => setFilter('graduationYear', /^\d{4}$/.test(e.target.value) ? Number(e.target.value) : undefined)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth size="small" label="Employment status" value={filters.status ?? ''} onChange={(e) => setFilter('status', e.target.value as AlumniStatus)}>
              <MenuItem value="">Any</MenuItem>
              {(Object.keys(ALUMNI_STATUS_LABELS) as AlumniStatus[]).map((s) => (
                <MenuItem key={s} value={s}>
                  {ALUMNI_STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Country of work"
              value={filters.countryOfWorkId ?? ''}
              onChange={(e) => setFilter('countryOfWorkId', e.target.value ? Number(e.target.value) : undefined)}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
            >
              <MenuItem value="">Any</MenuItem>
              {(lookups?.countries ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth size="small" label="Records" value={filters.archived ?? 'active'} onChange={(e) => setFilter('archived', e.target.value as Filters['archived'])}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }} flexWrap="wrap" useFlexGap>
              {activeChip('needsVerification', 'Needs verification')}
              {activeChip('followUpDue', 'Follow-up due')}
              <Button size="small" onClick={() => setFilters({ archived: 'active' })}>
                Clear filters
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {listQuery.isError && <Alert severity="error" sx={{ mb: 2 }}>Couldn't load alumni.</Alert>}

      <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={listQuery.data?.data ?? []}
          columns={columns}
          rowCount={listQuery.data?.meta.total ?? 0}
          loading={listQuery.isFetching}
          paginationMode="server"
          sortingMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(p) => navigate(`/alumni/${p.id}`)}
          slots={{ toolbar: TableToolbar }}
          localeText={{ noRowsLabel: 'No alumni match these filters.' }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>

      <AlumniFormDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        lookups={lookups}
        onSaved={(a) => {
          refresh();
          navigate(`/alumni/${a.id}`);
        }}
      />
      <AlumniImportDialog open={importOpen} onClose={() => setImportOpen(false)} lookups={lookups} onImported={refresh} />
      <AlumniExportDialog open={exportOpen} onClose={() => setExportOpen(false)} params={params} total={listQuery.data?.meta.total} />
    </Box>
  );
};
